import CustomModal from "@/components/modal/CustomModal";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import CustomInput from "./CustomInput";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { passwordChangeSchema } from "../schema/passwordSchema";
import type z from "zod";
import { CircleAlert } from "lucide-react";
import { cn } from "@/utils/cn";
import httpClient from "@/config/axios";

export default function PasswordChangeBtn() {
  const [isOpen, setIsOpen] = useState(false);

  const useFormMethods = useForm<{
    current_password: string;
    new_password: string;
    confirm_password: string;
  }>({
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    resolver: zodResolver(passwordChangeSchema),
  });

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors, isValid },
  } = useFormMethods;

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: { oldPassword: string; newPassword: string }) => {
      const res = await httpClient.put("/nurses/change-password", data);
      console.log(res);
      return res;
    },
    onSuccess: () => {
      setIsOpen(false);
      toast.success("비밀번호가 변경되었습니다.");
      reset();
    },
  });

  const onSubmitHandler = (data: z.infer<typeof passwordChangeSchema>) =>
    mutate(data);

  // error는 배열로 뺏음
  const errorMsg = Object.values(errors).map((error) => error.message);

  return (
    <>
      <CustomModal.Confirm
        isOpen={isOpen}
        onClose={() => {
          reset();
          clearErrors();
          setIsOpen(false);
        }}
        onConfirm={handleSubmit(onSubmitHandler)}
        isPending={isPending}
        pendingMsg="비밀번호 변경 중..."
        cancelText="닫기"
        confirmText="변경"
        // className="min-w-[550px]"
      >
        <FormProvider {...useFormMethods}>
          <div className="px-0">
            <div>
              <h3 className="text-[16px] text-center text-sig font-semibold font-pretendard mb-10">
                비밀번호 변경
              </h3>
            </div>
            {errorMsg.length > 0 && (
              <div className="text-red-600 text-sm mt-2 max-w-[400px] mb-10 flex  items-center gap-3">
                <CircleAlert />
                <span>{errorMsg[0]}</span>
              </div>
            )}

            <div className="flex flex-col gap-4 w-full ">
              <div className="grid grid-cols-[130px_2fr] gap-2 items-center">
                <span className="text-base font-pretendard font-medium">
                  기존 비밀번호
                </span>
                <CustomInput
                  type="password"
                  placeholder="현재 비밀번호"
                  {...register("current_password")}
                  className={cn(
                    "placeholder:text-gray-400 text-sm!",
                    errors.current_password &&
                      "border-red-300 shadow-lg shadow-red-300/20"
                  )}
                />
              </div>
              <div className="grid grid-cols-[130px_2fr] gap-2 items-center">
                <span className="text-base font-pretendard font-medium">
                  새 비밀번호
                </span>
                <CustomInput
                  type="password"
                  placeholder="새 비밀번호"
                  {...register("new_password")}
                  className={cn(
                    "placeholder:text-gray-400 text-sm!",
                    errors.new_password &&
                      "border-red-300 shadow-lg shadow-red-300/20",
                    isValid && "border-green-200 bg-green-100"
                  )}
                />
              </div>
              <div className="grid grid-cols-[130px_2fr] gap-2 items-center">
                <span className="text-base font-pretendard font-medium">
                  새 비밀번호 확인
                </span>
                <CustomInput
                  type="password"
                  placeholder="새 비밀번호 확인"
                  {...register("confirm_password")}
                  className={cn(
                    "placeholder:text-gray-400 text-sm!",
                    errors.confirm_password &&
                      "border-red-300 shadow-lg shadow-red-300/20",
                    isValid && "border-green-200 bg-green-100"
                  )}
                />
              </div>
            </div>

            {/* info */}
            <p className="text-sm text-gray-400 text-center my-10 max-w-[390px] mx-auto break-words leading-relaxed">
              * 새 비밀번호는 8~16자의 영어 대문자, 소문자, 특수문자, 숫자 중
              2가지 이상 조합하여 설정해주세요.
            </p>
          </div>
        </FormProvider>
      </CustomModal.Confirm>

      <Button
        variant="outline"
        type="button"
        className="shadow-none py-[10px] px-[14px] border-sig rounded-full bg-transparent flex justify-center items-center"
        onClick={() => setIsOpen(true)}
      >
        <span className="text-sig text-[16px]! font-semibold !font-pretendard ">
          비밀번호 변경
        </span>
      </Button>
    </>
  );
}
